import { mediaAttemptDiagnosticContext } from './mediaAttemptDiagnosticContext';
import type { MediaAttempt } from './MediaAttemptJournal';
import { AppState, Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { MediaDiagnosticHistory, mediaErrorCode } from './MediaDiagnosticHistory';
import { SupportDiagnosticBuffer } from './SupportDiagnosticBuffer';
import { bindMediaAttemptIdentity, finishMediaAttempt, flagMediaAttemptFailure, advanceMediaAttempt } from './mediaAttemptRuntime';

const history = new MediaDiagnosticHistory(() => new MMKV({ id: 'app-guincho-media-history' }));
let lifecycleBound = false;
export const bindMediaDiagnosticIdentity = (companyId?: string | null, userId?: string | null): void => {
  bindMediaAttemptIdentity(companyId, userId);
  try {
    history.bind(companyId, userId);
    if (!companyId || !userId || lifecycleBound) return;
    lifecycleBound = true;
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') window.addEventListener('pagehide', () => history.flush());
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') history.flush();
      });
    } else {
      AppState.addEventListener('change', state => { if (state !== 'active') history.flush(); });
    }
  } catch { /* Diagnóstico não bloqueia bootstrap. */ }
};
export const clearMediaDiagnosticHistory = (): void => history.clear();
export const createMediaDiagnosticRecorder = (input: Parameters<MediaDiagnosticHistory['recorder']>[0]) => {
  const record = history.recorder(input);
  return (phase: Parameters<typeof record>[0], error?: unknown) => {
    record(phase, error);
    if (!input.attemptId) return;
    if (phase === 'prepared' || phase === 'superseded' || phase === 'tombstone_blocked') finishMediaAttempt(input.attemptId);
    if (phase === 'capture_received' || phase === 'local_file_ready' || phase === 'prepare_started') advanceMediaAttempt(input.attemptId, phase);
    if (phase === 'local_file_failed' || phase === 'prepare_failed') finishMediaAttempt(input.attemptId, true, { phase, errorCode: mediaErrorCode(error) });
    if (phase === 'source_missing' || (phase === 'upload_failed' && mediaErrorCode(error) !== 'network')) {
      flagMediaAttemptFailure({ ...input, attemptId: input.attemptId, phase, errorCode: phase === 'source_missing' ? 'source_missing' : mediaErrorCode(error) });
    }
  };
};

export const createMediaSyncDiagnosticRecorder = (input: Parameters<MediaDiagnosticHistory['syncRecorder']>[0]) => {
  try {
    const record = history.syncRecorder(input);
    return (...args: Parameters<typeof record>): void => {
      try { record(...args); } catch { /* Falha do diagnóstico nunca altera o sync. */ }
    };
  } catch { return (..._args: Parameters<ReturnType<MediaDiagnosticHistory['syncRecorder']>>): void => {}; }
};

export const appendMediaDiagnosticEvents = (buffer: SupportDiagnosticBuffer, companyId: string, userId: string): void => {
  // Adicionar por último reserva os eventos de mídia contra o descarte dos logs gerais.
  try {
    history.snapshot(companyId, userId).forEach(event => buffer.record({
      occurredAt: new Date(event.at).toISOString(), level: event.errorCode ? 'error' : 'info',
      category: 'checklist_media', name: event.phase,
      data: { checklistId: event.checklistId, fieldId: event.fieldId, attemptId: event.attemptId ?? null,
        errorCode: event.errorCode ?? null, errorText: event.errorText ?? null, mediaRef: event.mediaRef ?? null,
        operationId: event.operationId ?? null, valueId: event.valueId ?? null,
        transport: event.transport ?? null, reason: event.reason ?? null,
        ...(event.phase === 'reference_committed' ? { referenceScope: 'local_prepared_not_remote_confirmation' } : {}),
      },
    }));
  } catch { /* Exportação geral continua disponível se o histórico falhar. */ }
};

/** Enriquece só a exportação: mantém os marcadores e o fluxo de captura/envio intactos. */
export const appendMediaAttemptDiagnosticEvents = (
  buffer: SupportDiagnosticBuffer, companyId: string, userId: string, attempts: readonly MediaAttempt[],
): void => {
  const events = history.snapshot(companyId, userId);
  const observedAt = Date.now();
  attempts.filter(attempt => attempt.identity === `${companyId}:${userId}`).forEach(attempt => buffer.record({
    occurredAt: new Date(attempt.at).toISOString(), level: 'warn', category: 'checklist_media',
    name: `attempt_${attempt.status}`, data: mediaAttemptDiagnosticContext(attempt, events, observedAt),
  }));
};
